import { supabase } from '../lib/supabase';

const auditLogService = {
  /**
   * Event types for audit logging
   */
  EVENT_TYPES: {
    LOGIN: 'login',
    LOGIN_FAILED: 'login_failed',
    LOGOUT: 'logout',
    REGISTER: 'register',
    PASSWORD_CHANGE: 'password_change',
    PASSWORD_RESET: 'password_reset',
    DEVICE_ADDED: 'device_added',
    DEVICE_REMOVED: 'device_removed',
    SESSION_TERMINATED: 'session_terminated',
    PERMISSION_CHANGE: 'permission_change',
    SECURITY_SETTINGS_CHANGE: 'security_settings_change',
    TWO_FACTOR_ENABLED: 'two_factor_enabled',
    TWO_FACTOR_DISABLED: 'two_factor_disabled',
  },

  /**
   * Create an audit log entry
   * @param {string} userId - User ID
   * @param {string} eventType - Event type from EVENT_TYPES
   * @param {string} eventDescription - Human-readable description
   * @param {object} options - Additional options
   */
  async createLog(userId, eventType, eventDescription, options = {}) {
    try {
      const { ipAddress, deviceInfo, success = true, metadata = {} } = options;

      const logData = {
        user_id: userId,
        event_type: eventType,
        event_description: eventDescription,
        ip_address: ipAddress || null,
        device_info: deviceInfo || this.getDeviceInfo(),
        success,
        metadata,
      };

      const { data, error } = await supabase?.from('audit_logs')?.insert(logData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating audit log:', error);
      return { data: null, error };
    }
  },

  /**
   * Get audit logs for current user with filtering and pagination
   * @param {object} filters - Filter options
   */
  async getLogs(filters = {}) {
    try {
      const {
        eventType,
        startDate,
        endDate,
        success,
        searchQuery,
        page = 1,
        pageSize = 20,
      } = filters;

      let query = supabase?.from('audit_logs')?.select('*', { count: 'exact' });

      // Apply filters
      if (eventType) {
        query = query?.eq('event_type', eventType);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      if (success !== undefined && success !== null) {
        query = query?.eq('success', success);
      }

      if (searchQuery) {
        query = query?.or(`event_description.ilike.%${searchQuery}%,event_type.ilike.%${searchQuery}%`);
      }

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query?.range(from, to);

      // Order by most recent first
      query = query?.order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        error: null,
        pagination: {
          page,
          pageSize,
          total: count || 0,
          totalPages: Math.ceil((count || 0) / pageSize),
        },
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { data: [], error, pagination: null };
    }
  },

  /**
   * Export audit logs to CSV
   * @param {object} filters - Filter options
   */
  async exportLogs(filters = {}) {
    try {
      const { eventType, startDate, endDate, success } = filters;

      let query = supabase?.from('audit_logs')?.select('*');

      // Apply filters
      if (eventType) {
        query = query?.eq('event_type', eventType);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      if (success !== undefined && success !== null) {
        query = query?.eq('success', success);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Convert to CSV
      const csv = this.convertToCSV(data || []);
      return { data: csv, error: null };
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      return { data: null, error };
    }
  },

  /**
   * Convert audit logs to CSV format
   * @param {Array} logs - Array of audit log objects
   */
  convertToCSV(logs) {
    if (!logs || logs?.length === 0) return '';

    const headers = ['Timestamp', 'Event Type', 'Description', 'Status', 'IP Address', 'Device', 'OS'];
    const rows = logs?.map((log) => [
      new Date(log?.created_at)?.toLocaleString(),
      log?.event_type,
      log?.event_description,
      log?.success ? 'Success' : 'Failed',
      log?.ip_address || 'N/A',
      log?.device_info?.browser || 'N/A',
      log?.device_info?.os || 'N/A',
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map((row) => row?.map((cell) => `"${cell}"`)?.join(',')),
    ]?.join('\n');

    return csvContent;
  },

  /**
   * Download CSV file
   * @param {string} csvContent - CSV content
   * @param {string} filename - Filename for download
   */
  downloadCSV(csvContent, filename = 'audit-logs.csv') {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document?.createElement('a');
    const url = URL?.createObjectURL(blob);

    link?.setAttribute('href', url);
    link?.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
  },

  /**
   * Get device information from browser
   */
  getDeviceInfo() {
    const userAgent = navigator?.userAgent || '';

    let browser = 'Unknown';
    if (userAgent?.includes('Chrome')) browser = 'Chrome';
    else if (userAgent?.includes('Firefox')) browser = 'Firefox';
    else if (userAgent?.includes('Safari')) browser = 'Safari';
    else if (userAgent?.includes('Edge')) browser = 'Edge';

    let os = 'Unknown';
    if (userAgent?.includes('Windows')) os = 'Windows';
    else if (userAgent?.includes('Mac')) os = 'macOS';
    else if (userAgent?.includes('Linux')) os = 'Linux';
    else if (userAgent?.includes('Android')) os = 'Android';
    else if (userAgent?.includes('iOS')) os = 'iOS';

    return { browser, os };
  },

  /**
   * Format event type for display
   * @param {string} eventType - Event type
   */
  formatEventType(eventType) {
    return eventType
      ?.split('_')
      ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
      ?.join(' ');
  },

  /**
   * Get event type color for UI
   * @param {string} eventType - Event type
   */
  getEventTypeColor(eventType) {
    const colorMap = {
      login: 'text-green-600',
      login_failed: 'text-red-600',
      logout: 'text-gray-600',
      register: 'text-blue-600',
      password_change: 'text-amber-600',
      password_reset: 'text-amber-600',
      device_added: 'text-purple-600',
      device_removed: 'text-orange-600',
      session_terminated: 'text-red-600',
      permission_change: 'text-indigo-600',
      security_settings_change: 'text-cyan-600',
      two_factor_enabled: 'text-green-600',
      two_factor_disabled: 'text-red-600',
    };

    return colorMap?.[eventType] || 'text-gray-600';
  },

  /**
   * Get event type icon
   * @param {string} eventType - Event type
   */
  getEventTypeIcon(eventType) {
    const iconMap = {
      login: 'LogIn',
      login_failed: 'XCircle',
      logout: 'LogOut',
      register: 'UserPlus',
      password_change: 'Key',
      password_reset: 'KeyRound',
      device_added: 'Smartphone',
      device_removed: 'SmartphoneOff',
      session_terminated: 'Ban',
      permission_change: 'Shield',
      security_settings_change: 'Settings',
      two_factor_enabled: 'ShieldCheck',
      two_factor_disabled: 'ShieldOff',
    };

    return iconMap?.[eventType] || 'Activity';
  },
};

export default auditLogService;